import { useEffect } from "react";
import { clsx } from "clsx";
import {
  voltZeroHero,
  voltZeroRateHero,
  voltZeroScorePhone,
} from "@/assets/images/offer/volt-zero";
import { Button } from "@/ui/components/Button";
import { Icon } from "@/ui/icons/Icon";
import { useContinueInAppModal } from "@/features/application/components";
import { trackingService } from "@/lib/tracking";
import { useLoanOffer } from "../../hooks";
import { OfferFaq } from "../../components/OfferFaq";
import { LandingHeading } from "../../components/LandingHeading";
import { LandingReviews } from "../../components/LandingReviews";
import { LandingSteps } from "../../components/LandingSteps";
import {
  DEMO_SCORE,
  DEMO_SCORE_DELTA,
  FAQ,
  HERO_CALLOUTS,
  MAX_SCORE,
  MIN_SCORE,
  RATE_TILES,
  REVIEWS,
  STEPS,
} from "./voltZeroContent";
import styles from "./VoltZeroLandingPage.module.scss";

const CALLOUT_POINTER =
  "M44.6318 0.624478L42.0683 2.60166C41.5433 3.00654 40.8991 3.22613 40.2361 3.22613H52.7469C52.0944 3.22613 51.4597 3.01338 50.939 2.62014L48.272 0.605998C47.193 -0.208811 45.7024 -0.201244 44.6318 0.624478Z";

const GAUGE_RADIUS = 72;
const GAUGE_CENTER = { x: 80, y: 80 };

// A point on the gauge's half circle, from its left end (0) to its right (1).
const gaugePoint = (progress: number) => {
  const angle = Math.PI * (1 - progress);
  return {
    x: GAUGE_CENTER.x + GAUGE_RADIUS * Math.cos(angle),
    y: GAUGE_CENTER.y - GAUGE_RADIUS * Math.sin(angle),
  };
};

function HeroCard() {
  return (
    <div className={styles.hero}>
      <img src={voltZeroHero} alt="" className={styles.heroImage} />
      {HERO_CALLOUTS.map((callout) => (
        <div
          key={callout.label}
          className={styles.callout}
          style={{
            left: `${callout.left}%`,
            top: `${callout.top}%`,
            transform: `translate(-50%, -50%) rotate(${callout.rotation}deg)`,
          }}
          aria-hidden
        >
          <span className={styles.calloutPill}>{callout.label}</span>
          <svg
            className={styles.calloutPointer}
            width="93"
            height="4"
            viewBox="0 0 93 3.226"
          >
            <path d={CALLOUT_POINTER} transform="rotate(180 46.5 1.613)" />
          </svg>
        </div>
      ))}
      <div className={styles.heroScrim} />
      <h1 className={styles.heroTitle}>
        Construiește-ți
        <br />
        istoricul de credit
      </h1>
    </div>
  );
}

// The phone's score climbing: a fixed marketing figure, the same for everyone.
function ScoreGauge() {
  const start = gaugePoint(0);
  const end = gaugePoint(1);
  const marker = gaugePoint((DEMO_SCORE - MIN_SCORE) / (MAX_SCORE - MIN_SCORE));
  const arc = (to: { x: number; y: number }) =>
    `M ${start.x} ${start.y} A ${GAUGE_RADIUS} ${GAUGE_RADIUS} 0 0 1 ${to.x} ${to.y}`;

  return (
    <div className={styles.gauge} aria-hidden>
      <svg
        width="160"
        height="88"
        viewBox="0 0 160 88"
        className={styles.gaugeArc}
      >
        <defs>
          <linearGradient id="voltZeroGauge" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0" stopColor="var(--color-error-100)" />
            <stop offset="0.5" stopColor="var(--color-brand-yellow)" />
            <stop offset="1" stopColor="var(--color-green-100)" />
          </linearGradient>
        </defs>
        <path d={arc(end)} className={styles.gaugeTrack} />
        <path d={arc(marker)} className={styles.gaugeProgress} />
        <circle
          cx={marker.x}
          cy={marker.y}
          r="7"
          className={styles.gaugeMarker}
        />
      </svg>
      <p className={styles.gaugeScore}>{DEMO_SCORE}</p>
      <p className={styles.gaugeDelta}>
        <span aria-hidden>↗</span>+{DEMO_SCORE_DELTA}
      </p>
    </div>
  );
}

/**
 * Volt Zero, offered instead of a credit: how it builds a credit history.
 * The product itself is activated in the app.
 */
export function VoltZeroLandingPage() {
  const { open: openContinueInAppModal, modal: continueInAppModal } =
    useContinueInAppModal();
  const { loanOfferState, isLoading: isLoanOfferLoading } = useLoanOffer(true);

  // event_id is the scoring id, so repeat visits on the same scoring dedupe.
  useEffect(() => {
    if (isLoanOfferLoading) {
      return;
    }
    void trackingService.trackEvent("volt_zero_shown", {
      event_id: loanOfferState?.id ?? undefined,
    });
  }, [isLoanOfferLoading]);

  return (
    <div className={styles.root}>
      <div className={styles.content}>
        <section className={styles.heroSection}>
          <HeroCard />
        </section>

        <section className={styles.growth}>
          <div className={styles.phone}>
            <img
              src={voltZeroScorePhone}
              alt=""
              className={styles.phoneImage}
            />
            <ScoreGauge />
          </div>
          <LandingHeading lines={["Un scor bun se", "construiește în timp"]} />
        </section>

        <section className={styles.rates}>
          <img src={voltZeroRateHero} alt="" className={styles.ratesImage} />
          <div className={styles.ratesContent}>
            <LandingHeading lines={["Scorul tău", "e prețul tău"]} isLight />
            <ul className={styles.rateTiles}>
              {RATE_TILES.map((tile) => (
                <li
                  key={tile.score}
                  className={clsx(
                    styles.rateTile,
                    styles[`rateTile--${tile.tone}`],
                  )}
                >
                  <Icon name="fico" size={24} />
                  <p className={styles.rateScore}>{tile.score}</p>
                  <p className={styles.rateDae}>{tile.dae}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className={styles.proof}>
          <div className={styles.proofBlock}>
            <LandingHeading lines={["De la primul pas", "la progres real"]} />
            <LandingReviews reviews={REVIEWS} />
          </div>

          <LandingSteps title="E ușor să începi" steps={STEPS} />
        </section>

        <section className={styles.faq}>
          <h2 className={styles.faqTitle}>Întrebări frecvente</h2>
          <OfferFaq items={FAQ} />
        </section>
      </div>

      <div className={styles.bottomSection}>
        <Button
          trackingId="volt_zero_landing_start"
          text="Vreau să încep"
          rightIcon={<Icon name="arrow-right" />}
          onClick={openContinueInAppModal}
          className={styles.startButton}
        />
      </div>
      {continueInAppModal}
    </div>
  );
}
